import React, { useState } from "react";
import { Grid, TextField, Typography, Button, Box, Checkbox, CircularProgress, useMediaQuery, useTheme, Link } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { LeaguePlatform } from "src/mockup/UserPreferencesData";
import { mobile } from "../../utils/platformUtils";
import { useSafeAccessAuth0Token } from "src/hooks/useSafeAccessAuth0Token";
import { sendEspnCode } from "src/api/sendEspnCode";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import LoginFormText from "./LoginFormText";

// Define the props interface for LoginFormEspn, similar to other login forms.
// We'll need props to manage authorization state, errors, and to trigger the API calls.
interface LoginFormEspnProps {
  platformSelected?: LeaguePlatform;
  loginError?: string;
  setLoginError?: (error: string) => void;
  handleEspnSubmit: (email: string, sessionId: string, passcode: string) => Promise<void>;
  isAuthorized: boolean;
  setIsAuthorized: (value: boolean) => void;
}

const LoginFormEspn: React.FC<LoginFormEspnProps> = ({ platformSelected, loginError, setLoginError, handleEspnSubmit, isAuthorized, setIsAuthorized }) => {
  // State for the form inputs
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sessionId, setSessionId] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { getSafeAccessAuth0Token } = useSafeAccessAuth0Token();

  // State to manage the UI flow and loading indicators
  const [isCodeSending, setIsCodeSending] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  /**
   * Resets the code input flow if the user changes the email after a code was sent.
   */
  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    // If the user starts typing in the email field after a code has been sent,
    // we should reset the UI to let them request a new code for the new email.
    if (isCodeSent) {
      setIsCodeSent(false);
      setCode("");
      setSessionId("");
      setLoginError(""); // Clear any previous errors as well
    }
  };

  /**
   * Handles the click on the "Send 6-digit Code" button.
   * This function will eventually call the backend to trigger the OTP email.
   */
  const handleSendCodeClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!email) {
      setLoginError("Please enter an email address.");
      return;
    }
    setIsCodeSending(true);
    setLoginError("");
    try {
      const accessToken = await getSafeAccessAuth0Token();
      if (!accessToken) {
        throw new Error("Authentication token not available.");
      }
      const response = await sendEspnCode(email, accessToken);
      setSessionId(response.sessionId);
      setIsCodeSent(true);
    } catch (error) {
      console.error("Error sending ESPN code:", error);
      setLoginError(error.message || "Failed to send code. Please try again.");
    } finally {
      setIsCodeSending(false);
    }
  };

  /**
   * Handles the click on the "Verify & Connect" button.
   * This function will eventually call the backend to verify the OTP code.
   */
  const handleVerifyAndConnectClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!code || code.length < 6) {
      setLoginError("Please enter the 6-digit code.");
      return;
    }
    setIsVerifying(true);
    setLoginError("");
    try {
      await handleEspnSubmit(email, sessionId, code);
    } catch (error) {
      console.error("Error verifying ESPN code:", error);
      setLoginError(error.message || "Invalid code. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Grid container spacing={1} justifyContent="center" sx={{ padding: 0 }}>
      <Grid item xs={12} sx={{ textAlign: "center" }}>
        <Typography sx={{ position: "relative", pb: 1 }} variant="h4" color={PureLightThemeCaaS.colors.alpha.orange[100]} textAlign="center">
          {loginError}
        </Typography>
        <Typography variant="h3" sx={{ color: "#000000", fontWeight: "bold" }}>
          ESPN
        </Typography>
      </Grid>

      {/* Email Input */}
      <Grid item xs={12} container justifyContent="center" alignItems="center" spacing={0}>
        <Grid item xs={10} sx={{ textAlign: "center", pb: 1 }}>
          <Typography variant="h6" color={PureLightThemeCaaS.colors.alpha.black[100]}>
            Email for ESPN OTP
          </Typography>
        </Grid>
        <Grid item xs={12} sx={{ textAlign: "center" }}>
          <TextField
            id="espn-email"
            size="small"
            value={email}
            onChange={handleEmailChange}
            disabled={isCodeSending}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (!isCodeSending && !isCodeSent && isAuthorized && email) {
                  // Simulate the button click event
                  handleSendCodeClick(e as any);
                }
              }
            }}
            sx={{
              width: "80%",
              borderRadius: 1,
              backgroundColor: PureLightThemeCaaS.colors.alpha.white[100],
            }}
            inputProps={{ style: { fontWeight: "bold" }, autoCapitalize: "off" }}
          />
        </Grid>
      </Grid>

      {/* Authorization Checkbox - moved up */}
      {!isCodeSent ? (
        <Grid item xs={12} container justifyContent="center" alignItems="center" spacing={0}>
          <Grid item xs={12} sx={{ textAlign: "center" }}>
            <Box display="flex" alignItems="flex-start" sx={{ width: "80%", margin: "0 auto" }}>
              <Checkbox
                checked={isAuthorized}
                onChange={(e) => setIsAuthorized(e.target.checked)}
                sx={{
                  padding: "0",
                  pt: 0.2,
                  boxShadow: "none",
                  marginRight: 1,
                  "&.Mui-checked": {
                    color: PureLightThemeCaaS.colors.alpha.black[100],
                  },
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              />
              <LoginFormText platformSelected={platformSelected.name} />
            </Box>
          </Grid>
        </Grid>
      ) : null}

      {/* Send Code Button and Status - moved down */}
      <Grid item xs={12} container flexDirection={"column"} justifyContent="center" alignItems="center" spacing={1} sx={{ mt: 0 }}>
        <Grid item>
          <Button
            variant="contained"
            size="large"
            onClick={handleSendCodeClick}
            disabled={isCodeSending || isCodeSent || !isAuthorized || !email}
            sx={{
              bgcolor: "#E52534",
              color: PureLightThemeCaaS.colors.alpha.white[100],
              width: "320px",
              height: "52px",
              p: 0,
              "&.Mui-disabled": {
                bgcolor: PureLightThemeCaaS.colors.alpha.gray[30],
                color: PureLightThemeCaaS.colors.alpha.black[50],
              },
              "&:hover, &:active, &:focus": {
                bgcolor: "#E52534",
                color: PureLightThemeCaaS.colors.alpha.white[100],
              },
            }}
          >
            {isCodeSending ? <CircularProgress size={24} sx={{ color: `${PureLightThemeCaaS.colors.alpha.white[100]}` }} /> : "Send 6-digit Code"}
          </Button>
        </Grid>
        {isCodeSent && (
          <Grid item>
            <Typography variant="h6" color="green">
              Code sent ✔
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* Step 2: Code Input and Verification (conditionally rendered) */}
      {isCodeSent && (
        <>
          <Grid item xs={12} container justifyContent="center" alignItems="center" spacing={1}>
            <Grid item xs={10} sx={{ textAlign: { xs: "center" } }}>
              <Typography variant="h6" pb={0} color={PureLightThemeCaaS.colors.alpha.black[100]}>
                Enter 6-digit code from ESPN
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ textAlign: "center" }}>
              <TextField
                id="espn-code"
                size="small"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={isVerifying}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (!isVerifying && isAuthorized && code.length >= 6) {
                      handleVerifyAndConnectClick(e as any);
                    }
                  }
                }}
                sx={{
                  // border: `1px solid ${PureLightThemeCaaS.colors.alpha.orange[100]}`,
                  width: "80%",
                  borderRadius: 1,
                  backgroundColor: PureLightThemeCaaS.colors.alpha.white[100],
                }}
                inputProps={{ style: { fontWeight: "bold", letterSpacing: "0.5em", textAlign: "center" }, maxLength: 6, autoCapitalize: "off" }}
              />
            </Grid>
          </Grid>

          {/* Verify Button and Status */}
          <Grid item xs={12} container flexDirection={isMobile ? "row" : "column"} justifyContent="center" alignItems="center" spacing={1} sx={{ mt: 0 }}>
            <Grid item>
              <Button
                variant="contained"
                size="large"
                onClick={handleVerifyAndConnectClick}
                disabled={isVerifying || !isAuthorized || code.length < 6}
                sx={{
                  // bgcolor: PureLightThemeCaaS.colors.alpha.orange[100],
                  bgcolor: "#E52534",
                  color: PureLightThemeCaaS.colors.alpha.white[100],
                  width: "320px",
                  height: "52px",
                  p: 0,
                  "&.Mui-disabled": {
                    bgcolor: PureLightThemeCaaS.colors.alpha.gray[30],
                    color: PureLightThemeCaaS.colors.alpha.black[50],
                  },
                  "&:hover, &:active, &:focus": {
                    bgcolor: "#E52534",
                    color: PureLightThemeCaaS.colors.alpha.white[100],
                  },
                }}
              >
                {isVerifying ? <CircularProgress size={24} sx={{ color: `${PureLightThemeCaaS.colors.alpha.white[100]}` }} /> : "Verify & Connect"}
              </Button>
            </Grid>
            {/* {isVerifying && (
              <Grid item>
                <Typography variant="h6" color="black">
                  Verifying...
                </Typography>
              </Grid>
            )} */}
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default LoginFormEspn;
